import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { keys, groupBy, isEmpty, isNil } from 'lodash';
// Import Store
import { useAppDispatch } from 'store';
import { deleteDynamicSegmentFilterSchemaList } from 'store/slices/dynamicSegment';
// Import UI Components
import { Alert, Icon, Title } from '@geneui/components';
// Import Components
import FilterGroup from './FilterGroup';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
// Import Services
import { getByHashKey } from '../service';
// Import Constants
import { l, ActionsIcons, ModalsClassNames, FilterComparisons } from 'constants/common';

const FilterSchema = (props) => {
    const {
        handleChangeActiveList,
        readOnly,
        setReadOnly,
        resetActiveList,
        editingFilter,
        setEditingFilter,
        filtersData,
        queryConfigurations,
        withoutActions,
        groupName,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

    const setDeleteModalOpen = () => {
        setIsOpenDeleteModal((prevState) => !prevState);
    };

    const handleDeleteGroupFilters = () => {
        dispatch(
            deleteDynamicSegmentFilterSchemaList({
                parentHashKeys: filtersData.filters.map((el) => el[0].parentHashKey),
                tabId: filtersData.id,
            }),
        );
        setDeleteModalOpen();
    };

    const { aggregated, notAggregated } = useMemo(() => {
        const filtersGroup = keys(filtersData.filters)
            .filter((key) => !isNil(filtersData.filters[key]))
            .map((key) => {
                const elem = filtersData.filters[key];
                const groupContainer = getByHashKey(elem[0]?.parentHashKey, queryConfigurations);
                const groupDisplayName = groupContainer?.parentDisplayName;
                const groupDescription = groupContainer?.parent?.description ?? '';
                const color = elem[0]?.comparison?.colorUI;
                const uiType = elem[0]?.comparison?.uiType;
                const schemaDeps = elem[0]?.comparison?.schemaDeps ?? [];

                return {
                    elem: elem.map((item, index) => ({
                        filter: item,
                        orderId: index,
                    })),
                    color,
                    uiType,
                    schemaDeps,
                    groupDisplayName,
                    groupDescription,
                    filterComparisionId: key,
                };
            });

        const { groupByAggregated = [], groupByNotAggregated = [] } = groupBy(filtersGroup, (item) =>
            item.elem[0].filter?.comparison?.isAggregated ? 'groupByAggregated' : 'groupByNotAggregated',
        );

        const regroupByAggregated = groupByNotAggregated.reduce<{
            aggregated: AnyObject[];
            notAggregated: AnyObject[];
        }>(
            (acc, item) => {
                const { excludeList = [], notExcludeList = [] } = groupBy(item.elem, (item) => {
                    return item.filter?.comparison?.comparison === FilterComparisons.Exclude
                        ? 'excludeList'
                        : 'notExcludeList';
                });

                if (excludeList.length) {
                    acc.aggregated.push({ ...item, elem: excludeList });
                }

                if (notExcludeList.length) {
                    acc.notAggregated.push({ ...item, elem: notExcludeList });
                }

                return acc;
            },
            { aggregated: [], notAggregated: [] },
        );

        return {
            aggregated: [...groupByAggregated, ...regroupByAggregated.aggregated],
            notAggregated: [...regroupByAggregated.notAggregated],
        };
    }, [queryConfigurations, filtersData]);

    return (
        <>
            <div className="crm-dynamic-segment-filter-schema-wrapper">
                <div
                    className="crm-dynamic-segment-filter-schema"
                    style={
                        {
                            '--kpi-group-rgb-color': 'rgba(60, 64, 67, 1)',
                            '--kpi-group-rgb-color-disabled': 'rgba(60, 64, 67, 0.5)',
                        } as React.CSSProperties
                    }
                >
                    <div className="crm-dynamic-segment-filter-schema__header crm-flex crm-justify-content-between crm-align-items-center">
                        <div className="crm-dynamic-segment-filter-schema__header__title">
                            {t(`Schema${groupName}`)}
                        </div>
                        <div
                            className={`crm-dynamic-segment-filter-schema__header__icons crm-flex crm-justify-content-between ${groupName
                                .replace(/\s+/g, '-')
                                .toLowerCase()}`}
                        >
                            {!withoutActions && (
                                <Icon type="bc-icon-trash" onClick={setDeleteModalOpen} disabled={readOnly} />
                            )}
                        </div>
                    </div>

                    {isEmpty(notAggregated) && (
                        <Alert
                            className="crm-dynamic-segment-filters-container-warning-message"
                            title={
                                withoutActions
                                    ? t(l.NotContainBasicFilters)
                                    : t(l.DynamicSegmentNotContainBasicFiltersWarningMessage)
                            }
                            iconProps={{ type: 'bc-icon-info' }}
                        />
                    )}
                    {notAggregated.map(
                        ({
                            elem,
                            color,
                            groupDisplayName,
                            groupDescription,
                            filterComparisionId,
                            uiType,
                            schemaDeps,
                        }) => (
                            <FilterGroup
                                key={`${filterComparisionId}_${filtersData.id}_${filtersData.seqId}`}
                                editingFilter={editingFilter}
                                setEditingFilter={setEditingFilter}
                                groupDisplayName={groupDisplayName}
                                resetActiveList={resetActiveList}
                                color={color}
                                uiType={uiType}
                                schemaDeps={schemaDeps}
                                description={groupDescription}
                                readOnly={readOnly}
                                withoutActions={withoutActions}
                                setReadOnly={setReadOnly}
                                list={elem}
                                handleChangeActiveList={handleChangeActiveList}
                                tabActiveIndex={filtersData.id}
                            />
                        ),
                    )}

                    <Title text={t(l.AggregatedFilters)} color={'base'} withLine />

                    {isEmpty(aggregated) && (
                        <Alert
                            className="crm-dynamic-segment-filters-container-warning-message"
                            title={
                                withoutActions
                                    ? t(l.NotContainAggregatedFilters)
                                    : t(l.DynamicSegmentNotContainAggregatedFiltersWarningMessage)
                            }
                            iconProps={{ type: 'bc-icon-info' }}
                        />
                    )}
                    {aggregated.map(
                        ({
                            elem,
                            color,
                            groupDisplayName,
                            groupDescription,
                            filterComparisionId,
                            uiType,
                            schemaDeps,
                        }) => (
                            <FilterGroup
                                key={`${filterComparisionId}_${filtersData.id}_${filtersData.seqId}`}
                                editingFilter={editingFilter}
                                setEditingFilter={setEditingFilter}
                                groupDisplayName={groupDisplayName}
                                resetActiveList={resetActiveList}
                                color={color}
                                uiType={uiType}
                                schemaDeps={schemaDeps}
                                description={groupDescription}
                                readOnly={readOnly}
                                withoutActions={withoutActions}
                                setReadOnly={setReadOnly}
                                list={elem}
                                handleChangeActiveList={handleChangeActiveList}
                                tabActiveIndex={filtersData.id}
                            />
                        ),
                    )}
                </div>
            </div>

            <ConfirmationModal
                isVisibleModal={isOpenDeleteModal}
                onOk={handleDeleteGroupFilters}
                onCancel={setDeleteModalOpen}
                questionLabel={t(l.DoYouWantToDeleteThisFilterSchemaFromDynamicSegmentFilterContainer)}
                titleText={t(l.DeleteFilterSchemaFromDynamicSegmentFilterContainer)}
                iconType={ActionsIcons.Delete}
                actionLabel={t(l.Delete)}
                className={ModalsClassNames.Delete}
            />
        </>
    );
};

FilterSchema.propTypes = {
    handleChangeActiveList: PropTypes.func,
    readOnly: PropTypes.bool,
    setReadOnly: PropTypes.func,
    resetActiveList: PropTypes.func,
    editingFilter: PropTypes.object,
    setEditingFilter: PropTypes.func,
    filtersData: PropTypes.object.isRequired,
    queryConfigurations: PropTypes.object.isRequired,
    withoutActions: PropTypes.bool,
    groupName: PropTypes.string,
};

FilterSchema.defaultProps = {
    readOnly: false,
    withoutActions: false,
    groupName: '',
};

export default FilterSchema;
